import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import { mapApiGender } from "@src/packages/patients/hooks/useCarePersons";
import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { buildContraceptionNavigationStack } from "./buildContraceptionNavigationStack";
import { transformContraceptionStateToRequest } from "./transformContraceptionStateToRequest";
import type { ContraceptionFlowRoute } from "./buildContraceptionNavigationStack";
import type { ContraceptionMethod } from "./options";

export interface ContraceptionDetail {
  id?: string;
  method: ContraceptionMethod;
  other?: string | null;
  contraceptionName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  daysTillPeriodAfterStopping?: number | null;
  notes?: string | null;
}

export interface ContraceptionFormValues {
  usesContraception: boolean | null;
  contraceptions: ContraceptionDetail[];
  contraceptionIdsToDelete: string[];
}

export interface ContraceptionState extends ContraceptionFormValues {
  navigationStack: ContraceptionFlowRoute[];
  currentNavigationIndex: number;
  userGender: "male" | "female" | null;
}

function readContraceptionEntries(
  patient: Patient | null | undefined,
  gender: "male" | "female" | null,
) {
  if (gender === "male") {
    const male = patient?.maleHistory;
    if (!male) return [];
    const plural = male.contraceptions;
    if (plural && plural.length > 0) return plural;
    const singular = (male as { contraception?: typeof male.contraceptions })
      .contraception;
    return singular ?? plural ?? [];
  }
  const ob = patient?.obGynHistory;
  if (!ob) return [];
  const plural = ob.contraceptions;
  if (plural && plural.length > 0) return plural;
  // Legacy singular field name, pre-regen — read via runtime cast so old
  // cached payloads still hydrate.
  const singular = (ob as { contraception?: typeof ob.contraceptions })
    .contraception;
  return singular ?? plural ?? [];
}

function defaultValuesFromPatient(
  patient: Patient | null | undefined,
  gender: "male" | "female" | null,
): ContraceptionFormValues {
  const entries = readContraceptionEntries(patient, gender);
  // Backend gap: `hasContraceptionHistory` is only in the OpenAPI schema for
  // obGynHistory. We read it off maleHistory via a runtime cast so the "no"
  // answer round-trips as soon as the backend surfaces the field.
  const maleFlag = (
    patient?.maleHistory as
      { hasContraceptionHistory?: boolean | null } | undefined
  )?.hasContraceptionHistory;
  const hasHistory: boolean | null =
    gender === "male"
      ? maleFlag != null
        ? maleFlag
        : entries.length > 0
          ? true
          : null
      : (patient?.obGynHistory?.hasContraceptionHistory ?? null);

  return {
    usesContraception: hasHistory,
    contraceptions: entries.map((c) => ({
      id: c.id ?? undefined,
      method: c.method ?? "other",
      other: c.other ?? null,
      contraceptionName: c.contraceptionName ?? null,
      startDate: c.startDate ?? null,
      endDate: c.endDate ?? null,
      daysTillPeriodAfterStopping:
        "daysTillPeriodAfterStopping" in c
          ? (c.daysTillPeriodAfterStopping ?? null)
          : null,
      notes: c.notes ?? null,
    })),
    contraceptionIdsToDelete: [],
  };
}

export function getResumeRoute(
  patient: Patient | null | undefined,
): ContraceptionFlowRoute {
  const userGender = mapApiGender(patient?.gender);
  const values = defaultValuesFromPatient(patient, userGender);

  if (values.usesContraception == null || values.contraceptions.length === 0) {
    return "ContraceptionMethods";
  }

  const base: ContraceptionState = {
    ...values,
    navigationStack: [],
    currentNavigationIndex: 0,
    userGender,
  };
  const stack = buildContraceptionNavigationStack(base);

  const screenComplete: Record<ContraceptionFlowRoute, () => boolean> = {
    ContraceptionMethods: () => values.contraceptions.length > 0,
    ContraceptionDetails: () => values.contraceptions.length > 0,
    ContraceptionComplete: () => false,
  };

  for (const route of stack) {
    if (route === "ContraceptionComplete") {
      continue;
    }
    if (!screenComplete[route]()) {
      return route;
    }
  }

  return stack[0] ?? "ContraceptionMethods";
}

interface ContraceptionContextValue {
  state: ContraceptionState;
  form: UseFormReturn<ContraceptionFormValues>;
  updateState: (updates: Partial<ContraceptionFormValues>) => void;
  resetState: () => void;
  save: () => Promise<void>;
}

const ContraceptionContext = createContext<ContraceptionContextValue | null>(
  null,
);

export function ContraceptionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient } = usePatient();
  const { mutateAsync } = useQueuedPatientUpdate();
  const userGender = mapApiGender(patient?.gender);

  const form = useForm<ContraceptionFormValues>({
    defaultValues: defaultValuesFromPatient(patient, userGender),
  });

  const formValues = form.watch();

  const state: ContraceptionState = useMemo(() => {
    const base: ContraceptionState = {
      ...formValues,
      navigationStack: [],
      currentNavigationIndex: 0,
      userGender,
    };
    return {
      ...base,
      navigationStack: buildContraceptionNavigationStack(base),
    };
  }, [formValues, userGender]);

  const updateState = useCallback(
    (updates: Partial<ContraceptionFormValues>) => {
      for (const [key, value] of Object.entries(updates)) {
        form.setValue(
          key as keyof ContraceptionFormValues,
          value as ContraceptionFormValues[keyof ContraceptionFormValues],
        );
      }
    },
    [form],
  );

  const save = useCallback(async () => {
    const values = form.getValues();
    const stateForTransform: ContraceptionState = {
      ...values,
      navigationStack: [],
      currentNavigationIndex: 0,
      userGender,
    };

    const transformed = transformContraceptionStateToRequest(stateForTransform);
    if (Object.keys(transformed).length === 0) {
      return;
    }

    const payload = {
      ...transformed,
      ...(transformed.maleHistory && {
        maleHistory: {
          id: patient?.maleHistory?.id,
          ...transformed.maleHistory,
        },
      }),
      ...(transformed.obGynHistory && {
        obGynHistory: {
          id: patient?.obGynHistory?.id,
          ...transformed.obGynHistory,
        },
      }),
    };

    await mutateAsync(payload);
  }, [form, userGender, mutateAsync, patient]);

  const resetState = useCallback(() => {
    form.reset(defaultValuesFromPatient(patient, userGender));
  }, [form, patient, userGender]);

  return (
    <ContraceptionContext.Provider
      value={{ state, form, updateState, resetState, save }}
    >
      {children}
    </ContraceptionContext.Provider>
  );
}

export function useContraception() {
  const ctx = useContext(ContraceptionContext);
  if (!ctx) {
    throw new Error(
      "useContraception must be used within ContraceptionProvider",
    );
  }
  return ctx;
}
